import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";
import { getValidMealArray } from "src/utils/constant";

const MealAccordion = ({ isMealData }) => {
  const [openIndex, setOpenIndex] = useState(0);

  const toggleAccordion = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  const convertToPerGram = (value = 0, quantity = 1) => {
    if (isNaN(value) || isNaN(quantity)) return 0;
    return parseFloat(((value / 100) * quantity).toFixed(2));
  };

  const calculateTotalCalories = (carb = 0, protein = 0, fat = 0) => {
    return (carb * 4 + protein * 4 + fat * 9).toFixed(2);
  };

  let mealData = getValidMealArray(isMealData);

  return (
    <div className="px-4 pb-4">
      {mealData &&
        mealData.length > 0 &&
        mealData.map((meal, mealIndex) => {
          const isOpen = openIndex === mealIndex;

          return (
            <div key={mealIndex} className="card mt-3 shadow-sm border-0">
              <div
                className="card-header text-white d-flex justify-content-between align-items-center"
                style={{ backgroundColor: "#04abbb", cursor: "pointer" }}
                onClick={() => toggleAccordion(mealIndex)}
              >
                <div className="d-flex align-items-center">
                  <strong>
                    {mealIndex + 1}. {meal.meal_name}
                  </strong>
                </div>

                <div className="d-flex align-items-center gap-2">
                  {isOpen ? (
                    <ChevronUp size={18} className="text-white" />
                  ) : (
                    <ChevronDown size={18} className="text-white" />
                  )}
                </div>
              </div>

              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    key="content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="card-body">
                      <ul className="list-group list-group-flush">
                        {meal.meal_food_array.map((altItem, altIdx) => {
                          const altFood = altItem.selected_food;
                          const altUnit = altItem.measurement_units;
                          return (
                            <li
                              key={altIdx}
                              className="list-group-item bg-light"
                            >
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <div className="fw-semibold">
                                    🍽 {altFood.name}
                                  </div>
                                  <small className="text-muted">
                                    {altItem.quantity}{" "}
                                    {altUnit.measurement_unit_name}
                                  </small>
                                </div>
                                <div className="text-end small">
                                  <div className="d-flex">
                                    <div>
                                      Carb:{" "}
                                      <strong>
                                        {convertToPerGram(
                                          altUnit?.carb,
                                          altItem?.quantity
                                        )}
                                        g{" "}
                                      </strong>
                                    </div>
                                    <div className="ms-2">
                                      Protein:{" "}
                                      <strong>
                                        {convertToPerGram(
                                          altUnit?.protein,
                                          altItem?.quantity
                                        )}
                                        g{" "}
                                      </strong>
                                    </div>
                                    <div className="ms-2">
                                      Fat:{" "}
                                      <strong>
                                        {convertToPerGram(
                                          altUnit?.fat,
                                          altItem?.quantity
                                        )}
                                        g{" "}
                                      </strong>{" "}
                                    </div>
                                  </div>
                                  <div className="fw-bold text-success mt-1">
                                    Total:{" "}
                                    {calculateTotalCalories(
                                      convertToPerGram(
                                        altUnit?.carb,
                                        altItem?.quantity
                                      ),
                                      convertToPerGram(
                                        altUnit?.protein,
                                        altItem?.quantity
                                      ),
                                      convertToPerGram(
                                        altUnit?.fat,
                                        altItem?.quantity
                                      )
                                    )}{" "}
                                    cal
                                  </div>
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                      {meal.meal_food_array_alt?.length > 0 && (
                        <>
                          <h6 className="mt-3">Alternative Food Options:</h6>
                          <ul className="list-group list-group-flush">
                            {meal.meal_food_array_alt.map((altItem, altIdx) => {
                              const altFood = altItem.selected_food;
                              const altUnit = altItem.measurement_units;

                              return (
                                <li
                                  key={altIdx}
                                  className="list-group-item bg-light"
                                >
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                      <div className="fw-semibold">
                                        🍽 {altFood.name}
                                      </div>
                                      <small className="text-muted">
                                        {altItem.quantity}{" "}
                                        {altUnit.measurement_unit_name}
                                      </small>
                                    </div>
                                    <div className="text-end small">
                                      <div className="d-flex">
                                        <div>
                                          Carb:{" "}
                                          <strong>
                                            {convertToPerGram(
                                              altUnit?.carb,
                                              altItem?.quantity
                                            )}
                                            g{" "}
                                          </strong>
                                        </div>
                                        <div className="ms-2">
                                          Protein:{" "}
                                          <strong>
                                            {convertToPerGram(
                                              altUnit?.protein,
                                              altItem?.quantity
                                            )}
                                            g{" "}
                                          </strong>
                                        </div>
                                        <div className="ms-2">
                                          Fat:{" "}
                                          <strong>
                                            {convertToPerGram(
                                              altUnit?.fat,
                                              altItem?.quantity
                                            )}
                                            g{" "}
                                          </strong>{" "}
                                        </div>
                                      </div>
                                      <div className="fw-bold text-success mt-1">
                                        Total:{" "}
                                        {calculateTotalCalories(
                                          convertToPerGram(
                                            altUnit?.carb,
                                            altItem?.quantity
                                          ),
                                          convertToPerGram(
                                            altUnit?.protein,
                                            altItem?.quantity
                                          ),
                                          convertToPerGram(
                                            altUnit?.fat,
                                            altItem?.quantity
                                          )
                                        )}{" "}
                                        cal
                                      </div>
                                    </div>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
    </div>
  );
};

export default MealAccordion;
